import React, { FC, useContext, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { StepContext } from '../../../creator/step.context';
import { FORM_FIELDS, FORM_STEPS } from '../../../../../constants';
import { SheetContent, SheetFooter, SheetHeader } from '../../components/form/components/focused.sheet';
import { needed as fieldNeeded } from '../../../steps/work/dependencies';
import { UNEMPLOYMENT_REASONS } from '../../../../../constants/sources';
import { SheetField } from '../../types';
import { WPViews } from '../../../steps/work/constants';
import { useWorkStepOptions } from '../../../../../store/selectors/formStore';
import { getCurrentField } from '../helpers';
import { isSatisfyOption } from '../../../../../helpers';
import {
    AnotherUnemploymentReasonFieldContainer,
    AreaOfEmploymentFieldContainer,
    EmployerInnFieldContainer,
    EmployerTypeFieldContainer,
    EmploymentTypeField,
    JobTitleFieldContainer,
    JobTypeFieldContainer,
    LastExperienceFieldsContainer,
    OrganizationNameFieldContainer,
    SalaryFieldContainer,
    UnEmploymentReasonFieldContainer,
    WorkAddressFieldContainer,
    WorkPhoneNumberFieldContainer,
} from './fields';

export const WorkStepSheetView: FC = React.memo(() => {
    const form = useFormContext();
    const { currentStepConfig, currentStep, setFocusedField, focusedField } = useContext(StepContext);
    const {
        watch,
        formState: { dirtyFields: _, errors },
    } = form;
    const { workPlaceView } = useWorkStepOptions();

    const stringifiedErrors = Object.keys(errors).join(' ');

    useEffect(() => {
        if (currentStepConfig.stepName === FORM_STEPS.WORK && focusedField) {
            setFocusedField(FORM_FIELDS.EMPLOYMENT_TYPE);
        }
    }, [currentStep]);

    useEffect(() => {
        if (
            isSatisfyOption(
                [FORM_FIELDS.LAST_EXPERIENCE_START_MONTH, FORM_FIELDS.LAST_EXPERIENCE_START_YEAR],
                focusedField || '',
            )
        ) {
            setFocusedField(FORM_FIELDS.LAST_EXPERIENCE);
        }
    }, [focusedField, currentStep]);

    const [employmentType, unemploymentReason] = watch([FORM_FIELDS.EMPLOYMENT_TYPE, FORM_FIELDS.UNEMPLOYMENT_REASON]);

    const isUnemploymentOtherReason = useMemo(
        () =>
            Boolean(
                fieldNeeded(FORM_FIELDS.ANOTHER_UNEMPLOYMENT_REASON, employmentType) &&
                    unemploymentReason === UNEMPLOYMENT_REASONS.OTHER,
            ),
        [employmentType, unemploymentReason],
    );

    const fieldsList = useMemo(() => {
        const workFields = [
            { name: FORM_FIELDS.ORGANIZATION_NAME, view: OrganizationNameFieldContainer },
            ...([WPViews.wpVisible, WPViews.wpVisibleManual].includes(workPlaceView)
                ? [
                      { name: FORM_FIELDS.EMPLOYER_TYPE, view: EmployerTypeFieldContainer },
                      { name: FORM_FIELDS.AREA_OF_EMPLOYMENT, view: AreaOfEmploymentFieldContainer },
                      { name: FORM_FIELDS.EMPLOYER_INN, view: EmployerInnFieldContainer },
                      { name: FORM_FIELDS.WORK_ADDRESS, view: WorkAddressFieldContainer },
                  ]
                : []),
            { name: FORM_FIELDS.LAST_EXPERIENCE, view: LastExperienceFieldsContainer },
            { name: FORM_FIELDS.WORK_PHONE, view: WorkPhoneNumberFieldContainer },
            { name: FORM_FIELDS.UNEMPLOYMENT_REASON, view: UnEmploymentReasonFieldContainer },
            { name: FORM_FIELDS.JOB_TITLE, view: JobTitleFieldContainer },
            { name: FORM_FIELDS.JOB_TYPE, view: JobTypeFieldContainer },
        ].reduce<Array<SheetField>>((prev, { name, view }) => {
            if (fieldNeeded(name, employmentType)) {
                prev.push({ name, view });
            }
            return prev;
        }, []);

        return [
            { name: FORM_FIELDS.EMPLOYMENT_TYPE, view: EmploymentTypeField },
            ...workFields,
            ...(isUnemploymentOtherReason
                ? [{ name: FORM_FIELDS.ANOTHER_UNEMPLOYMENT_REASON, view: AnotherUnemploymentReasonFieldContainer }]
                : []),
            { name: FORM_FIELDS.MONTHLY_SALARY, view: SalaryFieldContainer },
        ];
    }, [employmentType, isUnemploymentOtherReason, stringifiedErrors, workPlaceView, focusedField]);

    const headerTitleForExperienceField = useMemo(() => {
        if (([FORM_FIELDS.LAST_EXPERIENCE] as Array<string>).includes(focusedField || '')) {
            return 'Дата начала работы в организации';
        }
        return undefined;
    }, [focusedField]);

    const isDisabledNextBtnForExperienceField = useMemo(() => {
        if (([FORM_FIELDS.LAST_EXPERIENCE] as Array<string>).includes(focusedField || '')) {
            const errorFields = Object.keys(errors);
            return (
                errorFields.includes(FORM_FIELDS.LAST_EXPERIENCE_START_MONTH) ||
                errorFields.includes(FORM_FIELDS.LAST_EXPERIENCE_START_YEAR)
            );
        }
        return undefined;
    }, [focusedField, stringifiedErrors]);

    const { index: currentFieldIndex } = useMemo(
        () => getCurrentField(fieldsList, focusedField),
        [fieldsList, focusedField],
    );

    const nextBtnText = useMemo(
        () => (currentFieldIndex === fieldsList.length - 1 ? currentStepConfig.submitBtnText : undefined),
        [fieldsList, currentFieldIndex],
    );

    return (
        <>
            <SheetHeader title={headerTitleForExperienceField} />
            <SheetContent fieldsList={fieldsList} />
            <SheetFooter
                fieldsList={fieldsList}
                nextBtnDisabled={isDisabledNextBtnForExperienceField}
                hideFieldCounter={!!nextBtnText}
                nextBtnText={nextBtnText}
            />
        </>
    );
});

WorkStepSheetView.displayName = 'WorkStepSheetView';
